import {
  USER_PHOTO_SIZES,
  UserPhotoCard
} from "@/components/partial/Cards/UserPhotoCard";
import { ReservationCancel } from "@/components/reserve/ReservationCancel/ReservationCancel";
import ReserveCheckInButtonAdmin from "@/components/reserve/ReserveCheckin/ReserveCheckInButtonAdmin";
import { ReserveCheckTime } from "@/components/reserve/ReserveCheckin/ReserveCheckTime";
import ReserveEditModal from "@/components/reserve/ReserveEditModal";
import { useAdminReservationMutations } from "@/hooks/useAdminReservationMutations";
import { classNames } from "@/lib/classNames";
import { isRBFeature } from "@/lib/featureFlags";
import { formatStartEndSlotHours, groupBy, imageUrl } from "@/lib/utils";
import { JoinButton } from "@cmp-src/components/JoinButton/JoinButton";
import type { ISchedule } from "@gettactic/api";
import { canScheduleOthers, cn } from "@gettactic/helpers";
import { shouldEnablePrivacyForResourceType } from "@gettactic/helpers/src/resources/utils";
import { useAuthenticated } from "@gettactic/hooks";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { PencilIcon, TrashIcon } from "@heroicons/react/solid";
import { ActionIcon, Collapse, Menu } from "@mantine/core";
import { UserAvatar } from "@v2/components/display/UserAvatar/UserAvatar";
import { useRouter } from "next/router";
import React, { useMemo, useState } from "react";

type Props = {
  schedules: ISchedule[];
  fullVersion: boolean;
  highlightUserId?: string | undefined;
  isOrgPrivacyEnabled?: boolean;
};

function isRoom(type: string): boolean {
  return type === "meeting_room";
}

function isWorkspace(type: string): boolean {
  return type === "desk" || type === "workspace";
}

export function Groups({
  group,
  shouldCollapse,
  fullVersion,
  highlightUserId,
  isOrgPrivacyEnabled
}: {
  group: ISchedule[];
  shouldCollapse: boolean;
  fullVersion: boolean;
  highlightUserId?: string | undefined;
  isOrgPrivacyEnabled?: boolean;
}) {
  const {
    userContext: { authenticatedUser, currentOffice }
  } = useAuthenticated();
  const isRBFlag: boolean | undefined = isRBFeature(authenticatedUser);
  const usersInGroup = useMemo(() => {
    const users = group.flatMap((s) => s.slots.map((x) => x.user));
    // remove duplicates
    return users.filter((v, i, a) => a.findIndex((t) => t.id === v.id) === i);
  }, [group]);

  const isPrivacyEnabled = useMemo(() => {
    if (
      isOrgPrivacyEnabled &&
      shouldEnablePrivacyForResourceType(group[0].resource.type)
    ) {
      const isUserInGroup =
        usersInGroup.findIndex((x) => x.id === authenticatedUser?.user?.id) >
        -1;
      return !isUserInGroup;
    }
    return false;
  }, [authenticatedUser, usersInGroup]);

  const router = useRouter();
  const schedule = group[0];
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const { onCheckin, onCheckout, loading, targetSlot } =
    useAdminReservationMutations(schedule, authenticatedUser);
  const [opened, setOpened] = useState(!shouldCollapse);
  const slotForTime = group[0].slots.find(
    (x) => group[0].resource.id === x.resource.id && x.check_out_time
  );
  const hasCheckout = !!slotForTime;
  const officeHasRoomEnableCheckIn =
    authenticatedUser?.offices?.byId[currentOffice.id]
      ?.meeting_room_remote_checkin;
  const officeHasWorkspaceEnableCheckIn =
    authenticatedUser?.offices?.byId[currentOffice.id]
      ?.workspace_remote_checkin;

  const isHighlighted = React.useMemo(() => {
    return highlightUserId
      ? usersInGroup.some((user) => user.id === highlightUserId)
      : false;
  }, [highlightUserId, usersInGroup]);

  const hasAccessToJoinLink = useMemo(() => {
    const myId = authenticatedUser?.user?.id;
    const isAdmin = canScheduleOthers(authenticatedUser);
    const isAttendee = usersInGroup.some((user) => user.id === myId);
    const isOrganizer = schedule.organizer_id === myId;
    return isAdmin || isAttendee || isOrganizer;
  }, [authenticatedUser, usersInGroup, schedule]);

  return (
    <div
      className={cn(
        "mt-1 flex flex-col rounded-md border bg-gray-100 p-2",
        isHighlighted && "border-1 border-primary-500"
      )}
    >
      <div className="ml-1 flex cursor-pointer items-center space-x-2 text-left font-bold text-gray-900 text-sm">
        {!isPrivacyEnabled &&
        usersInGroup.length === 1 &&
        usersInGroup[0] &&
        shouldCollapse ? (
          <div
            className={classNames("mt-0.5", "h-5", "w-5", {
              grayscale: usersInGroup[0].roles.includes("deleted")
            })}
          >
            <UserPhotoCard
              size={USER_PHOTO_SIZES.ROUNDED_FULL_SM}
              profile_photo_url={
                usersInGroup[0].profile_photo_url
                  ? imageUrl(usersInGroup[0].profile_photo_url, "40x40_face")
                  : null
              }
              name={
                usersInGroup[0].name +
                (usersInGroup[0].roles.includes("deleted") ? " (inactive)" : "")
              }
            />
          </div>
        ) : undefined}
        <span
          onClick={() => setOpened((o) => !o)}
          className="whitespace-wrap grow"
        >
          {formatStartEndSlotHours(group[0])}
          {shouldCollapse && group.length > 1
            ? ` (${group.length} attendees)`
            : null}
        </span>
        <JoinButton
          onlineMeeting={schedule.online_meeting ?? null}
          iconOnly={true}
          hasAccess={hasAccessToJoinLink}
          wrapper={(children, icon, props) => (
            <ActionIcon
              onClick={props.onClick}
              className="h-5 w-5 text-tertiary hover:text-primary"
              size="sm"
              disabled={props.disabled}
            >
              {icon}
            </ActionIcon>
          )}
        />
        {targetSlot && fullVersion ? (
          <>
            {(targetSlot.capabilities.can_respond_presence_check_in ||
              targetSlot.capabilities.can_respond_presence_check_out ||
              targetSlot.check_out_time) &&
            ((isWorkspace(targetSlot.resource.type) &&
              officeHasWorkspaceEnableCheckIn &&
              !schedule.is_pending) ||
              (isRoom(targetSlot.resource.type) &&
                officeHasRoomEnableCheckIn &&
                !schedule.is_pending)) ? (
              <ReserveCheckInButtonAdmin
                targetSlot={targetSlot}
                loading={loading}
                onCheckin={onCheckin}
                onCheckout={onCheckout}
                schedule={schedule}
              />
            ) : null}
            {targetSlot.capabilities.can_edit ||
            targetSlot.capabilities.can_respond_cancelled ? (
              <Menu position="bottom-end" shadow="md" width={200}>
                <Menu.Target>
                  <ActionIcon>
                    <DotsVerticalIcon className="h-6 w-6 cursor-pointer text-tertiary" />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {/* <Menu.Item
                    onClick={() => {
                      setEditModal(true);
                    }}
                    disabled={!targetSlot.capabilities.can_edit}
                    className={
                      !targetSlot.capabilities.can_edit ? 'text-gray-300' : ''
                    }
                    icon={
                      <PencilIcon
                        className={`h-5 w-5 ${targetSlot.capabilities.can_edit ? 'text-tertiary' : 'text-disabled'}`}
                      />
                    }
                  >
                    Join
                  </Menu.Item> */}
                  <Menu.Item
                    onClick={() => {
                      setEditModal(true);
                    }}
                    disabled={!targetSlot.capabilities.can_edit}
                    className={
                      !targetSlot.capabilities.can_edit ? "text-gray-300" : ""
                    }
                    icon={
                      <PencilIcon
                        className={`h-5 w-5 ${targetSlot.capabilities.can_edit ? "text-tertiary" : "text-disabled"}`}
                      />
                    }
                  >
                    Edit Reservation
                  </Menu.Item>

                  <Menu.Item
                    disabled={!targetSlot.capabilities.can_respond_cancelled}
                    onClick={() => setConfirmDialog(true)}
                    icon={
                      <TrashIcon
                        className={`h-5 w-5 ${targetSlot.capabilities.can_respond_cancelled ? "text-tertiary" : "text-disabled"}`}
                      />
                    }
                  >
                    Cancel Reservation
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : null}
          </>
        ) : hasCheckout ? (
          <span className="mx-2 whitespace-nowrap rounded-full border border-tacticGray-400 bg-white px-2 py-1 text-center">
            <ReserveCheckTime
              time={slotForTime.check_out_time}
              type={"checkout"}
              clsNames={{
                general: "tactic-textGray-400 font-normal",
                time: "text-primary"
              }}
            />
          </span>
        ) : null}
      </div>
      {!isPrivacyEnabled ? (
        <Collapse in={opened}>
          <div className="flex flex-wrap">
            {usersInGroup.map((user) => (
              <div
                key={user.id}
                className={`mt-1 mr-1 flex rounded-md bg-gray-200 p-1 ${isRBFlag ? "cursor-pointer" : ""}`}
                onClick={() => isRBFlag && router.push(`/directory/${user.id}`)}
              >
                <div
                  className={classNames("-mt-0.5", "h-5", "w-5", {
                    grayscale: user.roles.includes("deleted")
                  })}
                >
                  <UserAvatar user={user} size={"xs"} />
                </div>
                <div className="flex max-w-[250px]">
                  <div className="ml-1 truncate">
                    {user.name}
                    {user.roles.includes("deleted") ? " (inactive)" : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Collapse>
      ) : null}
      <ReservationCancel
        schedules={[schedule]}
        setOpen={setConfirmDialog}
        open={confirmDialog}
      />
      <ReserveEditModal
        schedule={schedule}
        editModal={editModal}
        toggleModal={() => setEditModal(!editModal)}
      />
    </div>
  );
}
export default function SchedulesGroups({
  schedules,
  fullVersion,
  highlightUserId,
  isOrgPrivacyEnabled
}: Props) {
  const groups = Object.values(
    groupBy(schedules, (s: ISchedule) => `${s.resource.id}-${s.id}`)
  );
  // we want to collapse by default if are more than one group or if
  // the group has more than 4 members
  const shouldCollapse = groups.length > 1 || groups[0].length > 4;
  return (
    <div className="max-h-36 w-full overflow-y-auto overflow-x-hidden">
      {groups.map((group) => {
        return (
          <Groups
            key={group[0].id}
            group={group}
            shouldCollapse={shouldCollapse}
            fullVersion={fullVersion}
            highlightUserId={highlightUserId}
            isOrgPrivacyEnabled={isOrgPrivacyEnabled}
          />
        );
      })}
    </div>
  );
}
